import React, { useEffect, useState } from 'react';
import { Img } from 'react-image';
import { useNavigate, useParams } from 'react-router-dom';
import JobInfoCard from 'src/components/position/preview/JobInfoCard';
import { useGetLevelDistributionByIdQuery, useGetPositionByIdQuery } from 'src/hooks/position.hooks';
import { PositionStatus, UIPosition } from 'types';
import { Loader } from 'ui';
import MobileBottomButton from 'src/components/position/preview/MobileBottomButton';
import { useMutation, useQueryClient } from 'react-query';
import { confirmLevelFn } from 'src/apis/position';
import { useToast } from 'src/context/ToastProvider';
import { useUserContext } from 'src/context/UserProvider';
import useBottomCheck from 'src/hooks/utils/useBottomCheck';
import NewCreatingPositionLoader from 'src/components/position/NewCreatingPositionLoader';

const JobPreview = () => {
	const toast = useToast();
	const queryClient = useQueryClient();
	const navigate = useNavigate();
	const isAtBottom = useBottomCheck();
	const { jobId, companyId } = useParams();
	const { isAccountManagerLoggedIn } = useUserContext();
	const [disableBottomButtons, setDisableBottomButtons] = useState(false);

	useEffect(() => {
		queryClient.invalidateQueries(['getMyRegisteredPositions', '']);
		queryClient.refetchQueries(['getMyRegisteredPositions', '']);
	}, []);

	const [selectedJobLevel, setSelectedJobLevel] = useState<number>(0);

	const { isLoading, data: positionData, refetch } = useGetPositionByIdQuery(jobId);
	const { isLoading: levelLoading } = useGetLevelDistributionByIdQuery(jobId, d =>
		setSelectedJobLevel(d.recommendedLevel || 0),
	);
	const levelConfirmFn = useMutation(['confirmLevel'], confirmLevelFn, {
		onSuccess() {
			navigate(`/positions/${companyId}/${jobId}/candidates`);
		},
		onError(err: any) {
			toast({
				title: err.response.data.message,
				type: 'error',
			});
		},
	});
	const handleNext = () => {
		if (isAccountManagerLoggedIn) {
			// If auto recommendation is enabled, send lmsTechStack and subRole instead of level
			if (positionData?.position?.autoRecommendationEnable && positionData?.position?.lmsTechStack && Array.isArray(positionData.position.lmsTechStack) && positionData.position.lmsTechStack.length > 0) {
				levelConfirmFn.mutate({
					positionId: jobId || '',
					lmsTechStack: positionData.position.lmsTechStack.map((ts: any) => {
						// Ensure techStack is always a string (ObjectId)
						// Handle case where techStack might be an object
						let techStackId: string;
						const techStackValue = ts.techStack || ts;
						if (typeof techStackValue === 'string') {
							techStackId = techStackValue;
						} else if (techStackValue && typeof techStackValue === 'object') {
							// If it's an object, try to get _id or toString()
							const techStackObj = techStackValue as any;
							if (techStackObj._id) {
								techStackId = String(techStackObj._id);
							} else if (typeof techStackObj.toString === 'function') {
								techStackId = techStackObj.toString();
							} else {
								techStackId = String(techStackValue);
							}
						} else {
							techStackId = String(techStackValue);
						}
						return {
							techStack: techStackId,
							priority: ts.priority ?? 0,
						};
					}),
					subRole: positionData.position.subRole && Array.isArray(positionData.position.subRole) && positionData.position.subRole.length > 0 ? positionData.position.subRole : undefined,
				});
			} else {
				levelConfirmFn.mutate({ level: selectedJobLevel, positionId: jobId || '' });
			}
		} else {
			navigate(`/explainers/${positionData?.position?._id}`);
		}
	};

	useEffect(() => {
		setSelectedJobLevel(Math.floor(positionData?.position?.positionRequirement.levelThreshold || 0));
	}, [positionData]);

	useEffect(() => {
		if (
			positionData?.position?.hasConfirmedPublish ||
			positionData?.position?.company.isLead ||
			positionData?.position?.positionStatus === PositionStatus.CLOSED
		) {
			navigate(isAccountManagerLoggedIn ? `/positions/${companyId}/${jobId}/candidates` : '/positions');
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [positionData]);

	useEffect(() => {
		refetch();
	}, []);

	if (isLoading || levelLoading) return ( 
	// <Loader />
	<NewCreatingPositionLoader showAnimation={true} progressBar={false} customClassCss="!w-[30%] mx-auto " />
);

	return (
		<div className="max-w-[1366px] mx-auto dark:sm:bg-[#111E2C]">
			<div className="sm:gap-4 pt-[24px] pb-[85px] md:pb-[80px] md:px-6 w-full">
				<div className="md:max-w-[580px] w-full mx-auto sm:px-4">
					<p className="text-[#1C1C28] dark:text-[#E4E4EB] items-center gap-1 text-[16px] flex">
						Your job position is ready <Img src={'/assets/sparkles.svg'} />
					</p>
					<JobInfoCard
						data={positionData?.position as UIPosition}
						description={positionData?.position?.description}
						isLoading={isLoading}
						confirmLevelLoading={levelConfirmFn.isLoading}
						setDisableBottomButtons={setDisableBottomButtons}
					/>
					<div
						className="hidden md:block"
						style={{ borderBottom: '1.4px dotted rgba(199, 201, 217, 0.30)', paddingBottom: '20px' }}
					/>
				</div>
			</div>
			<div
				className="fixed bottom-0 inset-x-0 w-full transition-all md:max-w-[580px] mx-auto"
				style={{ backgroundColor: isAtBottom ? 'transparent' : 'white' }}
			>
				<MobileBottomButton
					nextBtnText={<>Next</>}
					disablePrevButton={levelConfirmFn.isLoading || disableBottomButtons}
					isLoading={levelConfirmFn.isLoading}
					disableNextButton={disableBottomButtons}
					onNext={handleNext}
					onBack={() => {
						navigate(
							isAccountManagerLoggedIn
								? `/edit-position/${positionData?.position?.company?._id}/${positionData?.position?._id}`
								: `/edit-position/${positionData?.position?._id}`,
						);
						window.scrollTo(0, 0);
					}}
				/>
			</div>
		</div>
	);
};

export default JobPreview;
